import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SettingsButton, SlideSettingsDrawer, DrawerSection, DrawerInput, DrawerNote, DrawerCode, DrawerCopyButton } from '../shared/SlideShared';
import { buildEmbedAuthorPrompt } from '../../../../lib/embedAuthorPrompt';
import type { EmbedEventHandlers } from '../shared/SlideShared';
import { isEmbedMessage, EmbedEventPayload } from '../../../../types';
import {
  parseEmbedResult,
  parseEmbedCheckpoint,
  embedScorePercent,
  EMBED_CHECKPOINT_LIMITS,
  type EmbedResult,
  type EmbedState,
} from '../../../../lib/embedProtocol';

/** How long `ready` waits for the saved-state read before lms-init goes out
 *  without it. The author prompt tells apps never to block on lms-init, so a
 *  slow read costs resume, not the app. */
const INIT_STATE_WAIT_MS = 2500;
import { isYouTubeUrl, getYouTubeEmbedUrl } from '../../../../lib/videoEmbed';
import { useAuth } from '../../../../context/AuthContext';
import {
  WebappSlideData,
  Theme,
} from '../slideTypes';

export function WebappSlide({
  data,
  theme,
  isEditing,
  onUpdate,
  slideId,
  lessonId,
  courseId,
  embedHandlers,
}: {
  data: WebappSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: WebappSlideData) => void;
  slideId?: string;
  lessonId?: string;
  courseId?: string;
  embedHandlers?: EmbedEventHandlers;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Stands in for a result's attemptId when the app didn't send one: one
  // viewing of the slide, plus the result itself, so a double-posted
  // `completed` is still one attempt. Apps should send their own.
  const mountIdRef = useRef(newMountId());
  // Distinct steps seen per attempt, for the per-attempt checkpoint cap.
  const checkpointStepsRef = useRef(new Map<string, Set<string>>());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { user } = useAuth();


  // A cross-origin embed spends a second or two blank while it boots, which
  // reads as broken. A themed veil holds the space until the iframe's load
  // event, with a timeout backstop so a document that never fires it (rare,
  // but possible) can't strand the veil on top of a working app.
  const [embedLoaded, setEmbedLoaded] = useState(false);

  // Auto-convert YouTube watch/share URLs to embed URLs for iframe compatibility
  const resolvedUrl = useMemo(() => {
    if (!data.url) return data.url;
    if (isYouTubeUrl(data.url)) {
      return getYouTubeEmbedUrl(data.url) || data.url;
    }
    return data.url;
  }, [data.url]);

  useEffect(() => {
    setEmbedLoaded(false);
    const t = setTimeout(() => setEmbedLoaded(true), 8000);
    return () => clearTimeout(t);
  }, [resolvedUrl]);

  // The learner's saved state for this lesson, read once per mount so it is
  // (usually) in hand by the time the app says `ready`. Through a ref because
  // the viewer rebuilds its handlers object every render.
  const handlersRef = useRef(embedHandlers);
  handlersRef.current = embedHandlers;
  const stateRef = useRef<Promise<EmbedState | null> | null>(null);
  useEffect(() => {
    if (isEditing || !slideId) {
      stateRef.current = null;
      return;
    }
    const read = handlersRef.current?.getEmbedState;
    stateRef.current = read ? read(slideId).catch(() => null) : null;
  }, [isEditing, slideId, resolvedUrl]);

  // Function to send initialization message to embed. Carries the learner
  // and, when the read has landed in time, their saved `state`; sent exactly
  // once per call, with `state: null` ("unknown") if the read is slow.
  const sendInitMessage = useCallback(() => {
    if (!iframeRef.current?.contentWindow || !resolvedUrl) return;
    const send = (state: EmbedState | null) => {
      if (!iframeRef.current?.contentWindow) return;
      try {
        const embedOrigin = new URL(resolvedUrl).origin;
        const [firstName, ...rest] = (user?.name || '').trim().split(/\s+/);
        const lastName = rest.join(' ');
        iframeRef.current.contentWindow.postMessage({
          type: 'lms-init',
          version: '1.0',
          payload: {
            slideId: slideId || 'unknown',
            lessonId: lessonId || 'unknown',
            courseId: courseId || 'unknown',
            user: user ? {
              id: user.id,
              name: user.name,
              firstName: firstName || '',
              lastName: lastName || '',
              email: user.email,
              role: user.role,
            } : null,
            state,
          }
        }, embedOrigin);
      } catch (e) {
        console.error('Failed to send init message to embed:', e);
      }
    };
    const pending = stateRef.current;
    if (!pending) {
      send(null);
      return;
    }
    let sent = false;
    const timer = setTimeout(() => {
      if (sent) return;
      sent = true;
      send(null);
    }, INIT_STATE_WAIT_MS);
    void pending.then((state) => {
      if (sent) return;
      sent = true;
      clearTimeout(timer);
      send(state);
    });
  }, [resolvedUrl, slideId, lessonId, courseId, user]);

  useEffect(() => {
    if (isEditing || !resolvedUrl) return;

    // Handle messages from embedded webapp
    const handleMessage = (event: MessageEvent) => {
      // Validate origin
      try {
        const embedOrigin = new URL(resolvedUrl || 'about:blank').origin;
        if (event.origin !== embedOrigin) return;
      } catch {
        return;
      }

      // Check if it's a valid LMS embed message
      if (!isEmbedMessage(event.data)) {
        // Legacy: log unrecognized messages for debugging
        console.log('Message from webapp (unrecognized format):', event.data);
        return;
      }

      const payload = event.data.payload as EmbedEventPayload;
      const url = data.url || '';
      const id = slideId || 'unknown';

      // Handle different event types
      switch (payload.event) {
        case 'ready':
          // Notify parent that embed is ready, provide sendInit function
          if (embedHandlers?.onEmbedReady) {
            embedHandlers.onEmbedReady(id, url, sendInitMessage);
          } else {
            // Auto-send init if no handler
            sendInitMessage();
          }
          break;

        case 'started':
          embedHandlers?.onEmbedEvent?.(id, url, { status: 'started' });
          break;

        case 'progress':
          embedHandlers?.onEmbedEvent?.(id, url, {
            status: 'in_progress',
            progress: payload.percent
          });
          break;

        case 'completed': {
          // An optional `result` carries the marks. A malformed one is
          // dropped, but loudly: completion still counts, and the console
          // names what was wrong (the embed checker shows the same reason).
          let result: EmbedResult | undefined;
          if (payload.result !== undefined) {
            const parsed = parseEmbedResult(payload.result);
            // `in` rather than `parsed.ok`: the repo's tsconfig isn't strict, and
            // boolean-literal narrowing needs strictNullChecks.
            if ('result' in parsed) {
              result = {
                ...parsed.result,
                attemptId: parsed.result.attemptId ?? autoAttemptId(mountIdRef.current, parsed.result),
              };
            } else {
              console.warn(`Embed result ignored: ${parsed.reason}`, payload.result);
            }
          }
          embedHandlers?.onEmbedEvent?.(id, url, {
            status: 'completed',
            progress: 100,
            score: embedScorePercent(payload.score, result),
            result,
          });
          break;
        }

        case 'checkpoint': {
          // A moment mid-run. Malformed means dropped, loudly, like a result.
          const parsed = parseEmbedCheckpoint(payload);
          if (!('checkpoint' in parsed)) {
            console.warn(`Embed checkpoint ignored: ${parsed.reason}`, payload);
            break;
          }
          const checkpoint = {
            ...parsed.checkpoint,
            attemptId: parsed.checkpoint.attemptId ?? `auto:${mountIdRef.current}`,
          };
          const steps = checkpointStepsRef.current.get(checkpoint.attemptId) ?? new Set<string>();
          if (!steps.has(checkpoint.step) && steps.size >= EMBED_CHECKPOINT_LIMITS.maxPerAttempt) {
            console.warn(`Embed checkpoint ignored: more than ${EMBED_CHECKPOINT_LIMITS.maxPerAttempt} distinct steps in attempt ${checkpoint.attemptId}`);
            break;
          }
          steps.add(checkpoint.step);
          checkpointStepsRef.current.set(checkpoint.attemptId, steps);
          embedHandlers?.onEmbedCheckpoint?.(id, url, checkpoint);
          break;
        }

        case 'advance':
          // The webapp's own final button is the exit. Same code path as the
          // chrome's Done — no tracking here, 'completed' already did that.
          embedHandlers?.onEmbedAdvance?.(id);
          break;

        case 'submitted':
          embedHandlers?.onEmbedEvent?.(id, url, {
            submissionData: payload.submission.content,
            submissionType: payload.submission.type
          });
          break;

        case 'error':
          console.error('Embed error:', payload.message);
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [data.url, isEditing, slideId, embedHandlers, sendInitMessage]);

  // Settings drawer content
  const settingsDrawer = isEditing && (
    <SlideSettingsDrawer
      isOpen={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      title="Webapp Settings"
    >
      <DrawerSection label="Webapp URL">
        <DrawerInput
          type="url"
          value={data.url}
          onChange={(e) => onUpdate({ ...data, url: e.target.value })}
          placeholder="https://your-webapp.vercel.app"
        />
      </DrawerSection>
      <DrawerSection label="Title (accessibility)">
        <DrawerInput
          type="text"
          value={data.title || ''}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Descriptive title..."
        />
      </DrawerSection>
      {/* The prompt leads; the three snippets below are reference for anyone
          hand-writing it. Most of these apps are built by prompting an agent,
          so the briefing is the main path, not an extra. */}
      <DrawerNote title="Send this to whoever built the app">
        <p style={{ margin: '0 0 8px' }}>
          A full briefing — all three messages, the origin rule for this slide's
          URL, and the mistakes that fail silently. Paste it into the AI chat
          the app was built in.
        </p>
        <DrawerCopyButton
          // resolvedUrl, not data.url — the runtime origin check derives from
          // the resolved form, and normalising a youtu.be share link to an
          // embed URL moves the origin. The prompt must name the origin the
          // slide will actually enforce, or its whole point is lost.
          text={buildEmbedAuthorPrompt({ url: resolvedUrl })}
          label="Copy setup prompt"
        />
      </DrawerNote>
      <DrawerNote title="Track learner progress">
        <p style={{ margin: 0 }}>Notify the LMS when a learner completes an activity:</p>
        <DrawerCode>
{`window.parent.postMessage({
  type: 'lms-embed-event',
  version: '1.0',
  payload: { event: 'completed',
             score: 85 }
}, '*');`}
        </DrawerCode>
      </DrawerNote>
      <DrawerNote title="Let learners continue from your own button">
        <p style={{ margin: 0 }}>
          End on one button and fire this from its click handler — the learner
          moves to the next slide without hunting for Done up in the bar:
        </p>
        <DrawerCode>
{`window.parent.postMessage({
  type: 'lms-embed-event',
  version: '1.0',
  payload: { event: 'advance' }
}, '*');`}
        </DrawerCode>
        <p style={{ margin: 0 }}>
          Send <code>'completed'</code> when they <em>reach</em> your closing
          screen, <code>'advance'</code> when they <em>click</em> to leave it.
          Firing <code>'advance'</code> on arrival pulls them off their own
          results before they've read them.
        </p>
      </DrawerNote>
      <DrawerNote title="Receive learner info">
        <p style={{ margin: 0 }}>
          Post <code>event: 'ready'</code> on load; the LMS replies with an{' '}
          <code>lms-init</code> message containing the user:
        </p>
        <DrawerCode>
{`window.addEventListener('message', (e) => {
  if (e.source !== window.parent) return;
  if (e.data?.type !== 'lms-init') return;
  const user = e.data.payload?.user;
  if (!user?.id) return;
  // user: { id, name, firstName,
  //         lastName, email, role }
});`}
        </DrawerCode>
      </DrawerNote>
    </SlideSettingsDrawer>
  );

  // Empty state — no URL set yet
  if (!data.url) {
    return (
      <div className="relative h-full group" style={{ backgroundColor: theme.colors.surface }}>
        <div className="h-full flex flex-col items-center justify-center gap-4">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent, #3b82f6) 10%, transparent)' }}
          >
            <svg className="w-8 h-8" style={{ color: 'var(--accent, #3b82f6)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
          </div>
          <div className="text-center">
            <p className="font-medium text-sm mb-0.5" style={{ color: theme.colors.text }}>Webapp Embed</p>
            <p className="text-xs" style={{ color: theme.colors.textMuted }}>No URL configured</p>
          </div>
          {isEditing && (
            <button
              onClick={() => setSettingsOpen(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: 'var(--accent, #2563eb)' }}
            >
              Configure URL
            </button>
          )}
        </div>
        {settingsDrawer}
      </div>
    );
  }

  // WYSIWYG: show the live iframe + settings button on hover
  return (
    <div className="relative h-full group">
      {!isEditing ? (
        <motion.div
          className="relative w-full h-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <iframe
            ref={iframeRef}
            src={resolvedUrl}
            title={data.title || 'Embedded webapp'}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            loading="lazy"
            onLoad={() => setEmbedLoaded(true)}
          />
          {/* Themed veil over the embed's boot blankness — a slide that shows
              its own ground and a pulse reads as "coming", not "broken". */}
          <AnimatePresence>
            {!embedLoaded && (
              <motion.div
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4"
                style={{ backgroundColor: theme.colors.background }}
                initial={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.35 } }}
                aria-label="Loading interactive"
              >
                <motion.div
                  className="h-1.5 w-40 rounded-full"
                  style={{ backgroundColor: theme.colors.textMuted, opacity: 0.4 }}
                  animate={{ opacity: [0.2, 0.55, 0.2] }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                />
                <span className="text-xs font-medium" style={{ color: theme.colors.textMuted }}>
                  Loading interactive…
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ) : (
        <iframe
          ref={iframeRef}
          src={resolvedUrl}
          title={data.title || 'Embedded webapp'}
          className="w-full h-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          loading="lazy"
        />
      )}
      {isEditing && <SettingsButton onClick={() => setSettingsOpen(true)} />}
      {settingsDrawer}
    </div>
  );
}

function autoAttemptId(mountId: string, r: EmbedResult): string {
  return `auto:${mountId}:${r.earned}/${r.possible}:${hashString(JSON.stringify(r.items ?? []))}`;
}

function newMountId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}

/** Short, stable, non-cryptographic: only has to tell two results apart. */
function hashString(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}
